import styled from 'styled-components';
import { Card, Skeleton } from '@mui/material';
import { Formik, Form, Field, ErrorMessage } from 'formik';
import Text from '../../../components/common/Text';
import DefaultField from '../../referred/components/DefaultField';
import DefaultPhone from '../../../components/common/DefaultPhone';
import DefaultSelect from '../../../components/common/DefaultSelect';
import { guestSchema } from '../../../utils/schemas';
import { useCountries } from '../hooks/useCountries';
import { useEducationalOffer } from '../hooks/useEducationalOffer';
import { useSnackbar } from 'react-simple-snackbar';
import { optionsStyle, successStyle } from '../../../utils/snackStyles';
import { CheckCircleRounded, Error } from '@material-ui/icons';
import { useQueryParams } from '../../../hooks/useQueryParams';
import { addReferral } from '../../../redux/api/behaviour/referrals';

export const GuestForm = ({
    contact,
    submitGuestInformation,
    isContactLoading,
}) => {
    const params = useQueryParams();
    const { countries, loadingCountries, errorCountries } = useCountries();
    const { programs, loadingPrograms, errorPrograms } = useEducationalOffer();
    const [openSuccessSnackbar] = useSnackbar(successStyle);
    const [openErrorSnackbar] = useSnackbar(optionsStyle);
    const campaign = params.get('utm_campaign') ?? 'mkt';
    const content = params.get('utm_content') ?? 'mkt';
    const medium = params.get('utm_medium') ?? 'Referidos 2026';
    const source = params.get('utm_source') ?? 'Referido';

    const initialValues = {
        name: '',
        lastName: '',
        email: '',
        phone: '',
        country: '',
        city: '',
        program: '',
        position: '',
        educationLevel: '',
        privacyConsent: true,
    };

    const positions = [
        { name: 'Abogado/a' },
        { name: 'Administrador' },
        { name: 'Consultor' },
        { name: 'Coordinador' },
        { name: 'Director' },
        { name: 'Director ejecutivo' },
        { name: 'Gerente' },
        { name: 'Presidente' },
        { name: 'Propietario' },
        { name: 'Supervisor' },
        { name: 'Otro' },
    ];

    const educationLevels = [
        { name: 'Secundaria' },
        { name: 'Terciario' },
        { name: 'Universitaria' },
        { name: 'Maestría - Posgrado' },
        { name: 'Doctorado' },
    ];

    const handleSubmit = async (values, { resetForm }) => {
        if (!contact) {
            openErrorSnackbar(
                <div
                    style={{
                        display: 'flex',
                        alignItems: 'center',
                        gap: '8px',
                    }}
                >
                    <Error style={{ fontSize: '20px', color: '#B31D15' }} />
                    Error: No se pudo obtener la información del contacto
                </div>,
                [3000]
            );
            return;
        }

        const program = programs.find((p) => p.productId === values.program);

        const body = {
            partner_id: contact?.partner_id,
            utm_medium: medium,
            utm_source: source,
            utm_campaign: campaign,
            utm_content: content,
            name: values.name,
            last_name: values.lastName,
            email: values.email,
            phone: values.phone,
            country_code: values.country,
            city: values.city,
            id_prod_mkt: program.productId,
            program_name: program.productTitle,
            education_level: values.educationLevel,
            position: values.position,
        };

        const isError = await submitGuestInformation(body);
        addReferral(
            {
                referer: {
                    partner_id: contact?.partner_id,
                    name: contact?.name,
                },
                guest: {
                    first_name: values.name,
                    last_name: values.lastName,
                    email: values.email,
                    phone: values.phone,
                    program: program.productTitle,
                    program_id: program.productId,
                },
            },
            values.email
        );

        if (!isError) {
            openSuccessSnackbar(
                <div
                    style={{
                        display: 'flex',
                        alignItems: 'center',
                        gap: '8px',
                    }}
                >
                    <CheckCircleRounded
                        style={{ fontSize: '20px', color: '#4BB543' }}
                    />
                    Información enviada correctamente!
                </div>,
                [3000]
            );

            setTimeout(() => {
                window.open('https://www.aden.org/gracias/', '_blank');
            }, 2000);

            resetForm();
        } else {
            openErrorSnackbar(
                <div
                    style={{
                        display: 'flex',
                        alignItems: 'center',
                        gap: '8px',
                    }}
                >
                    <Error style={{ fontSize: '20px', color: '#B31D15' }} />
                    Error al enviar la información
                </div>,
                [3000]
            );
        }
    };

    const hasErrors = errorPrograms || errorCountries;
    const isLoading = loadingCountries || loadingPrograms || isContactLoading;

    return (
        <GuestFormWrapper>
            <GuestFormHeader>
                <Text
                    color="#b31d15"
                    fontSize="32px"
                    fontWeight="700"
                    textAlign="center"
                >
                    Ingresa aquí tus datos como invitado
                </Text>
            </GuestFormHeader>

            <Formik
                initialValues={initialValues}
                onSubmit={handleSubmit}
                validationSchema={guestSchema}
            >
                {({ values, setFieldValue }) => (
                    <FormContainer>
                        <FormContent>
                            <FieldWrapper>
                                <DefaultField
                                    name="name"
                                    type="text"
                                    label="Nombre"
                                    required
                                    variant="outlined"
                                />
                                <DefaultField
                                    name="lastName"
                                    type="text"
                                    variant="outlined"
                                    label="Apellido"
                                    required
                                />
                            </FieldWrapper>
                            <DefaultField
                                name="email"
                                type="email"
                                variant="outlined"
                                label="Correo electrónico"
                                required
                            />
                            <DefaultPhone
                                styled={styledInput}
                                name="phone"
                                label="Whatsapp"
                                value={values?.phone}
                                setFieldValue={setFieldValue}
                            />
                            <FieldWrapper>
                                <DefaultSelect
                                    name="position"
                                    label="Cargo"
                                    variant="outlined"
                                    valueKey="name"
                                    labelKey="name"
                                    options={positions}
                                    placeholder="Seleccionar cargo"
                                />
                                <DefaultSelect
                                    name="educationLevel"
                                    label="Nivel de estudios"
                                    variant="outlined"
                                    valueKey="name"
                                    labelKey="name"
                                    options={educationLevels}
                                    placeholder="Seleccionar estudios"
                                />
                            </FieldWrapper>
                            {loadingCountries ? (
                                <SelectSkeletonWrapper>
                                    <SkeletonLabel
                                        variant="text"
                                        width="15%"
                                        height={20}
                                    />
                                    <Skeleton
                                        variant="rectangular"
                                        width="100%"
                                        height={56}
                                        style={{ borderRadius: '10px' }}
                                    />
                                </SelectSkeletonWrapper>
                            ) : errorCountries ? (
                                <ErrorWrapper>
                                    <ErrorLabel>País</ErrorLabel>
                                    <ErrorBox>
                                        <Error
                                            style={{
                                                fontSize: '20px',
                                                color: '#B31D15',
                                            }}
                                        />
                                        <ErrorText>
                                            No se pudieron cargar los países
                                        </ErrorText>
                                    </ErrorBox>
                                </ErrorWrapper>
                            ) : (
                                <DefaultSelect
                                    name="country"
                                    label="País"
                                    variant="outlined"
                                    options={countries || []}
                                    valueKey="code"
                                    labelKey="name"
                                    placeholder="Selecciona un país"
                                />
                            )}
                            <DefaultField
                                name="city"
                                type="text"
                                variant="outlined"
                                label="Ciudad"
                                required
                            />
                            {loadingPrograms ? (
                                <SelectSkeletonWrapper>
                                    <SkeletonLabel
                                        variant="text"
                                        width="25%"
                                        height={20}
                                    />
                                    <Skeleton
                                        variant="rectangular"
                                        width="100%"
                                        height={56}
                                        style={{ borderRadius: '10px' }}
                                    />
                                </SelectSkeletonWrapper>
                            ) : errorPrograms ? (
                                <ErrorWrapper>
                                    <ErrorLabel>Programa a referir</ErrorLabel>
                                    <ErrorBox>
                                        <Error
                                            style={{
                                                fontSize: '20px',
                                                color: '#B31D15',
                                            }}
                                        />
                                        <ErrorText>
                                            No se pudieron cargar los programas
                                        </ErrorText>
                                    </ErrorBox>
                                </ErrorWrapper>
                            ) : (
                                <DefaultSelect
                                    name="program"
                                    label="Programa a referir"
                                    variant="outlined"
                                    options={programs || []}
                                    groupBy="categoryProduct"
                                    valueKey="productId"
                                    labelKey="productTitle"
                                    placeholder="Selecciona un programa"
                                />
                            )}
                        </FormContent>

                        <CheckboxWrapper>
                            <Field
                                type="checkbox"
                                name="privacyConsent"
                                id="privacyConsent"
                                checked={values.privacyConsent}
                            />
                            <CheckboxLabel htmlFor="privacyConsent">
                                Consiento el uso de mis datos para los fines
                                indicados en la{' '}
                                <PrivacyLink
                                    href="https://www.aden.org/privacy-policy/"
                                    target="_blank"
                                    rel="noopener noreferrer"
                                >
                                    Política de Privacidad
                                </PrivacyLink>
                                , así como para recibir publicidad por email y/o
                                el Whatsapp de su entidad.
                            </CheckboxLabel>
                        </CheckboxWrapper>
                        <ErrorMessage
                            name="privacyConsent"
                            component={ConsentError}
                        />

                        <ButtonWrapper>
                            <SubmitButton
                                type="submit"
                                value="Enviar mi información"
                                disabled={hasErrors || isLoading}
                            />
                        </ButtonWrapper>
                    </FormContainer>
                )}
            </Formik>
        </GuestFormWrapper>
    );
};

const GuestFormWrapper = styled(Card)`
    background-color: #ffffff;
    box-shadow:
        0px 3px 6px 0px #0000003b,
        0px 3px 6px 0px #00000029;
    border-radius: 20px;
    width: 100%;
    padding: 2rem;
    display: flex;
    flex-direction: column;
    justify-content: space-around;
    align-items: center;
    gap: 1rem;
    border: 1px solid #a8a8a8;
    box-sizing: border-box;

    @media (max-width: 768px) {
        padding: 1.5rem;
    }

    @media (max-width: 480px) {
        padding: 1rem;
    }
`;

const GuestFormHeader = styled.div`
    width: 100%;

    @media (max-width: 768px) {
        padding-top: 1rem;
        padding-bottom: 2rem;
    }
`;

const FormContainer = styled(Form)`
    display: flex;
    flex-direction: column;
    gap: 1rem;
    width: 100%;
`;

const FormContent = styled.div`
    display: flex;
    flex-direction: column;
    gap: 1.5rem;
    width: 100%;
`;

const ButtonWrapper = styled.div`
    margin-top: 1rem;
    display: flex;
    justify-content: center;
    gap: 1rem;
`;

const FieldWrapper = styled.div`
    display: flex;
    justify-content: center;
    gap: 1rem;
`;

const SubmitButton = styled.input`
    cursor: pointer;
    height: 45px;
    border-radius: 10px;
    padding: 0 2rem;
    background-color: #cd2118;
    color: #fff;
    font-size: 16px;
    font-weight: 600;
    border: 0;
    transition: background-color 0.3s ease;

    &:disabled {
        background-color: #a2a2a2;
        color: #f1f1f1;
        opacity: 0.5;
        cursor: not-allowed;

        &:hover {
            background-color: #a2a2a2;
        }
    }

    &:hover:not(:disabled) {
        background-color: #b31d15;
    }
`;

const styledInput = {
    width: '100%',
    padding: '10px 55px',
    borderRadius: '10px',
};

const SelectSkeletonWrapper = styled.div`
    display: flex;
    flex-direction: column;
    gap: 0.5rem;
    width: 100%;
`;

const SkeletonLabel = styled(Skeleton)`
    margin-bottom: 0.25rem;
`;

const ErrorWrapper = styled.div`
    display: flex;
    flex-direction: column;
    gap: 0.5rem;
    width: 100%;
`;

const ErrorLabel = styled.label`
    font-size: 0.875rem;
    color: rgba(0, 0, 0, 0.54);
    margin-bottom: 0.25rem;
`;

const ErrorBox = styled.div`
    display: flex;
    align-items: center;
    gap: 0.75rem;
    padding: 1rem;
    background-color: #ffebee;
    border: 1px solid #ef5350;
    border-radius: 10px;
    width: 93%;
`;

const ErrorText = styled.span`
    font-size: 0.875rem;
    color: #b31d15;
    font-weight: 500;
`;

const CheckboxWrapper = styled.div`
    display: flex;
    align-items: flex-start;
    gap: 0.5rem;
    margin-top: 0.7rem;

    input[type='checkbox'] {
        margin-top: 0.25rem;
        width: 18px;
        height: 18px;
        cursor: pointer;
        flex-shrink: 0;
    }
`;

const CheckboxLabel = styled.label`
    font-size: 0.875rem;
    color: rgba(0, 0, 0, 0.87);
    line-height: 1.5;
    cursor: pointer;
`;

const PrivacyLink = styled.a`
    color: #b31d15;
    text-decoration: underline;
    font-weight: 500;

    &:hover {
        color: #cd2118;
    }
`;

const ConsentError = styled.span`
    font-size: 0.75rem;
    color: #b31d15;
    display: block;
`;
